<script lang="ts">
import { ref, watch, onUpdated, onBeforeMount, onBeforeUnmount, computed } from 'vue';
import type { NavigationNode } from '../../types/link';
import { isMobile, isTablet } from '../../utils/breakpoints';
import Button from '../Button1XP/Button1XP.vue';
import MainNavItem from './components/MainNavItem/MainNavItem.vue';
import Teaser from './components/Teaser/Teaser.vue';
import CategoryLink from './components/CategoryLink/CategoryLink.vue';
import type { NavContentTeaser, NavContentTeaserDocData } from '../../types/navContentTeaser';
import { useStoreTranslations } from '../../store/translations/translations';

type Props = {
  items: NavigationNode[];
  activeNodes: string[];
  teasers?: {
    [key: string]: NavContentTeaser | NavContentTeaserDocData;
  };
};

type LinkClick = {
  event: Event;
  shouldActivateLevel: number;
  activeLinkId: string;
  activeLinkLabel: string;
};
</script>

<script setup lang="ts">
const props = defineProps<Props>();
const emits = defineEmits(['openHeader']);

const storeTranslations = useStoreTranslations();

const isMainNavOpen = ref(false);
const activeNodesRef = ref<string[]>([]); // collection of the active nodes in all levels
const activeNavLevelRef = ref<number>(0); // the depth/level of the active navigation
const activeNavLabel = ref<string>(); // the label of the active navigation
const shouldBeVisible = ref(); // default 3rd level navigation shown when the second level nav is activated
const mainNav = ref();
const flyOutStyle = ref({});
const flyOutPosition = ref<number>(0);

const visibleItems = computed(() => {
  return props.items.filter((item) => !item.hideInNavigation);
});

const handleNavItemClick = (linkClick: LinkClick) => {
  const lastClickedItemIndex = activeNodesRef.value[activeNodesRef.value.length - 1];

  if (lastClickedItemIndex === linkClick.activeLinkId) {
    if (linkClick.shouldActivateLevel === 2 && !(isMobile.value || isTablet.value)) {
      isMainNavOpen.value = false;
    }

    if (linkClick.shouldActivateLevel === 3) {
      return;
    }

    const parentIndex = visibleItems.value.findIndex((item) => {
      if (item.children) {
        const visibleFirstLevelItemChildren = filterItems(item.children);
        return visibleFirstLevelItemChildren.find((child) => child._id === linkClick.activeLinkId);
      }
    });

    activeNavLevelRef.value = linkClick.shouldActivateLevel - 1;
    activeNodesRef.value.pop();

    if (visibleItems.value[parentIndex]?.children) {
      const shouldBeVisibleFirstLevelChildren = filterItems(
        visibleItems.value[parentIndex].children,
      );

      if (shouldBeVisibleFirstLevelChildren[0] && shouldBeVisibleFirstLevelChildren[0].children) {
        const shouldBeVisibleSecondLevelChildren = filterItems(
          shouldBeVisibleFirstLevelChildren[0].children,
        );

        shouldBeVisible.value = shouldBeVisibleFirstLevelChildren?.length
          ? shouldBeVisibleSecondLevelChildren[0]._id
          : null;
      }
    }
  } else {
    if (linkClick.shouldActivateLevel === 2 && !(isMobile.value || isTablet.value)) {
      isMainNavOpen.value = true;
    }

    if (activeNavLevelRef.value === linkClick.shouldActivateLevel) {
      if (!(isMobile.value || isTablet.value)) {
        const linkWithActiveChildren = visibleItems.value.find(
          (item) => item._id === linkClick.activeLinkId,
        );

        if (linkWithActiveChildren?.children) {
          const visibleActiveChildren = filterItems(linkWithActiveChildren.children);

          if (visibleActiveChildren[0] && visibleActiveChildren[0].children) {
            const visibleActiveChildrenChildren = filterItems(visibleActiveChildren[0].children);

            shouldBeVisible.value = visibleActiveChildrenChildren?.length
              ? visibleActiveChildren[0]._id
              : null;
          }
        }
      }

      activeNodesRef.value.pop();
      activeNodesRef.value.push(linkClick.activeLinkId);
    } else {
      if (!(isMobile.value || isTablet.value)) {
        const linkWithActiveChildren = visibleItems.value.find(
          (item) => item._id === linkClick.activeLinkId,
        );

        if (linkWithActiveChildren?.children) {
          const visibleActiveChildren = filterItems(linkWithActiveChildren.children);

          if (visibleActiveChildren[0] && visibleActiveChildren[0].children) {
            const visibleActiveChildrenChildren = filterItems(visibleActiveChildren[0].children);

            shouldBeVisible.value = visibleActiveChildrenChildren.length
              ? visibleActiveChildren[0]._id
              : null;
          }
        }
      }

      if (activeNavLevelRef.value > linkClick.shouldActivateLevel) {
        activeNodesRef.value = [];
      }

      activeNavLevelRef.value = linkClick.shouldActivateLevel;
      activeNodesRef.value.push(linkClick.activeLinkId);
    }
  }

  activeNavLabel.value =
    activeNavLabel.value === linkClick.activeLinkLabel ? '' : linkClick.activeLinkLabel;

  if (linkClick.shouldActivateLevel > 2) {
    shouldBeVisible.value = null;
  }

  if (linkClick.shouldActivateLevel === 0) {
    isMainNavOpen.value = false;
  }
};

const handleBackClick = () => {
  activeNavLevelRef.value = activeNavLevelRef.value - 1;
  activeNodesRef.value.pop();
  activeNavLabel.value = visibleItems.value.find(
    (item) => item._id === activeNodesRef.value[activeNodesRef.value.length - 1],
  )?.label;
};

const endIcon = (id: string) => {
  if (isMobile.value || isTablet.value) {
    return 'chevron_right';
  } else {
    if (activeNodesRef.value.find((item) => item === id)) {
      return 'keyboard_arrow_up';
    } else {
      return 'keyboard_arrow_down';
    }
  }
};

const filterItems = (items: NavigationNode[]) => {
  return items.filter((item) => !item.hideInNavigation);
};

const sortedItems = (items: NavigationNode[]) => {
  return items.sort((a, b) => (a.position || 0) - (b.position || 0));
};

const findFlyOutPosition = (ref) => {
  flyOutPosition.value = ref.getBoundingClientRect().x;
};

const focusClickOutsideClose = (e) => {
  const clickedElement = e.target;
  const clickOnMainNav = clickedElement.closest('.MainNav__mainNavLink');
  const clickInFlyOut = clickedElement.closest('.MainNav__flyOut');

  if (!(clickOnMainNav || clickInFlyOut)) {
    if (isMainNavOpen.value) {
      e.preventDefault();
      isMainNavOpen.value = false;
    }
  }
};

watch(isMainNavOpen, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emits('openHeader', newValue);

    if (!newValue) {
      activeNavLevelRef.value = 0;
      activeNodesRef.value = [];
    }
  }
});

watch(flyOutPosition, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    flyOutStyle.value = { transform: 'translateX(-' + flyOutPosition.value + 'px)' };
  }
});

onBeforeMount(() => {
  if (!(isMobile.value || isTablet.value)) {
    window.addEventListener('resize', () => {
      findFlyOutPosition(mainNav.value);
    });
    window.addEventListener('click', (e) => {
      focusClickOutsideClose(e);
    });
    window.addEventListener('keydown', (e) => {
      focusClickOutsideClose(e);
    });
  }
});

onUpdated(() => {
  findFlyOutPosition(mainNav.value);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', () => {
    findFlyOutPosition(mainNav.value);
  });
  window.removeEventListener('click', (e) => {
    focusClickOutsideClose(e);
  });
  window.removeEventListener('keydown', (e) => {
    focusClickOutsideClose(e);
  });
});
</script>

<template>
  <div
    :class="[
      $style.container,
      activeNavLevelRef >= 2 && (isMobile || isTablet) && $style.secondLevelOpened,
    ]"
  >
    <div v-if="activeNavLevelRef >= 2 && (isMobile || isTablet)" :class="$style.navHeader">
      <Button size="s" start-icon="arrow_back" variant="linkStatic" @click="handleBackClick" />
      <h2 v-if="activeNavLevelRef" v-html="activeNavLabel" />
    </div>
    <div :class="$style.navContainer">
      <nav
        ref="mainNav"
        :aria-label="
          storeTranslations.translations?.['mainNav.firstLevel'] || 'main navigation first level'
        "
        :class="[!!activeNodesRef[activeNodesRef.length - 1] && $style.hasActiveSubMenu]"
      >
        <ul v-if="visibleItems?.length">
          <li v-for="item in sortedItems(visibleItems)" :key="item.position">
            <template v-if="item">
              <MainNavItem
                :class="[
                  $style.mainNavLink,
                  item._id && activeNodes?.includes(item._id) ? $style.visitedPage : '',
                  activeNodesRef.find((activeItem) => activeItem === item._id)
                    ? $style.activeLink
                    : '',
                ]"
                :controls="'mainNavSecondLevel' + item._id"
                :end-icon="item._id && endIcon(item._id)"
                :item="item"
                :level="2"
                @item-click="(e) => handleNavItemClick(e)"
              />
            </template>
            <div
              v-if="item.children && filterItems(item.children).length"
              ref="flyOut"
              :class="[
                $style.flyOut,
                $style.flyOut__secondLevel,
                activeNavLevelRef >= 2 &&
                  activeNodesRef[activeNodesRef.length - 1] === item._id &&
                  $style.activeNav,
                activeNavLevelRef >= 3 &&
                  item._id &&
                  activeNodesRef.includes(item._id) &&
                  !(isMobile || isTablet) &&
                  $style.thirdLevelOpened,
                activeNavLevelRef >= 3 && (isMobile || isTablet) && $style.thirdLevelOpened,
                activeNavLevelRef >= 3 && (isMobile || isTablet) && $style.activeNav,
                !item.children.some((subItem) => subItem.children) && $style.lastLevelNav,
              ]"
              :style="!(isMobile || isTablet) && flyOutStyle"
            >
              <h2
                v-if="!(isMobile || isTablet) && activeNavLevelRef >= 2"
                v-html="visibleItems.find((item) => item._id === activeNodesRef[0])?.label"
              />
              <nav
                :id="'mainNavSecondLevel' + item._id"
                :aria-label="storeTranslations.translations?.['mainNav.secondLevel']"
                :class="!item.children.some((subItem) => subItem.children) && $style.lastNav"
              >
                <ul v-if="item.children?.length">
                  <li
                    v-for="subItem in sortedItems(item.children).filter(
                      (subItem) => !subItem.hideInNavigation,
                    )"
                    :key="subItem.position"
                    :class="[
                      subItem._id && activeNodes?.includes(subItem._id) && $style.visitedPageLink,
                      (activeNodesRef.find((activeItem) => activeItem === subItem._id) ||
                        shouldBeVisible === subItem._id) &&
                        $style.activeMenuLink,
                    ]"
                  >
                    <template v-if="subItem && !subItem.hideInNavigation">
                      <MainNavItem
                        :class="[
                          $style.mainNavLink,
                          !(subItem.media || subItem.image) && $style.navItemNoImage,
                        ]"
                        :controls="'mainNavThirdLevel' + subItem._id"
                        end-icon="chevron_right"
                        :is-last-nav-item="!subItem.children"
                        :item="subItem"
                        :level="3"
                        @item-click="(e) => handleNavItemClick(e)"
                      />
                    </template>
                    <div
                      v-if="subItem.children?.length"
                      :class="[
                        $style.flyOut__thirdLevel,
                        !(isMobile || isTablet) &&
                          shouldBeVisible === subItem._id &&
                          $style.activeNav,
                        activeNavLevelRef >= 3 &&
                          activeNodesRef[activeNodesRef.length - 1] === subItem._id &&
                          $style.activeNav,
                        $style.lastLevelNav,
                      ]"
                    >
                      <nav
                        :id="'mainNavThirdLevel' + subItem._id"
                        :aria-label="
                          storeTranslations.translations?.['mainNav.thirdLevel'] && subItem?.label
                            ? storeTranslations.translations?.['mainNav.thirdLevel'] +
                              subItem?.label
                            : ''
                        "
                        :class="$style.lastNav"
                      >
                        <ul v-if="subItem.children?.length">
                          <li
                            v-for="subSubItem in sortedItems(subItem.children).filter(
                              (subSubItem) => !subSubItem.hideInNavigation,
                            )"
                            :key="subSubItem.position"
                            :class="[
                              subSubItem._id &&
                                activeNodes?.includes(subSubItem._id) &&
                                $style.visitedPageLink,
                              activeNodesRef.find((activeItem) => activeItem === subSubItem._id) &&
                                $style.activeMenuLink,
                            ]"
                          >
                            <a
                              v-if="subSubItem.label && subSubItem.url"
                              :id="subSubItem._id"
                              :class="
                                !(subSubItem.media || subSubItem.image) && $style.navItemNoImage
                              "
                              :href="subSubItem.url"
                            >
                              <div :class="$style.teaserImg">
                                <img
                                  v-if="subSubItem.media || subSubItem.image"
                                  alt=""
                                  loading="lazy"
                                  :src="subSubItem.media || subSubItem.image"
                                />
                              </div>
                              <span v-html="subSubItem.label" />
                              <span class="accessible-link-text">{{ subSubItem.label }}</span>
                            </a>
                          </li>
                          <li v-if="subItem.children?.length && subItem.url">
                            <CategoryLink
                              :class="$style.categoryLink"
                              :icon-name="'arrow_forward'"
                              :icon-size="'medium'"
                              :label="
                                (storeTranslations.translations?.['mainNav.viewAll'] ||
                                  'Übersicht') +
                                ' ' +
                                subItem.label
                              "
                              :url="subItem.url"
                            />
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </li>
                  <li
                    v-if="
                      item.children?.length &&
                      item.url &&
                      !item.children.some((subItem) => subItem.children)
                    "
                  >
                    <CategoryLink
                      :class="$style.categoryLink"
                      :icon-name="'arrow_forward'"
                      :icon-size="'medium'"
                      :label="
                        (storeTranslations.translations?.['mainNav.viewAll'] || 'Übersicht') +
                        ' ' +
                        item.label
                      "
                      :url="item.url"
                    />
                  </li>
                </ul>
                <CategoryLink
                  v-if="
                    item.children?.length &&
                    item.url &&
                    item.children.some((subItem) => subItem.children)
                  "
                  :label="
                    (storeTranslations.translations?.['mainNav.viewAll'] || 'Übersicht') +
                    ' ' +
                    item.label
                  "
                  :url="item.url"
                />
                <Teaser
                  v-if="
                    item?.teaserId && teasers && teasers[item.teaserId] && (isMobile || isTablet)
                  "
                  :class="[$style.navTeaser]"
                  :teaser="teasers[item.teaserId]"
                />
                <Teaser
                  v-if="
                    item?.teaserId &&
                    teasers &&
                    teasers[item.teaserId] &&
                    !(isMobile || isTablet) &&
                    item.children.some((subItem) => subItem.children)
                  "
                  :class="[$style.navTeaser]"
                  :teaser="teasers[item.teaserId]"
                />
              </nav>
              <Teaser
                v-if="
                  item?.teaserId &&
                  teasers &&
                  teasers[item.teaserId] &&
                  !(isMobile || isTablet) &&
                  !item.children.some((subItem) => subItem.children)
                "
                :class="[$style.navTeaser]"
                :teaser="teasers[item.teaserId]"
              />
            </div>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>
<style lang="scss" module src="./MainNav.scss" />
